import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useAuthStore from "@/store/authStore";
import { useMemo } from "react";

// Greeting mappings based on personality traits
const TRAIT_GREETINGS = {
  Corporate: [
    "Let's optimize our workflow",
    "Ready to enhance productivity",
    "Time to elevate performance",
    "Strategic alignment required",
    "Let's maximize efficiency",
    "Executive decision time",
    "Boardroom ready",
  ],
  Professional: [
    "Prepared to assist",
    "Ready to collaborate",
    "Let's achieve excellence",
    "Ready to deliver results",
    "Time to excel",
    "Let's innovate together",
    "Prepared for success",
  ],
  Direct: [
    "What's the objective",
    "Target acquired",
    "Mission parameters",
    "Execute the plan",
    "Priority identified",
    "Action required",
    "Let's accomplish this",
  ],
  Respectful: [
    "Honored to assist",
    "At your service",
    "How may I serve you",
    "Ready to support",
    "Your wishes are my command",
    "Let's honor your vision",
    "Prepared to help",
  ],
  Creative: [
    "Ready to innovate",
    "What masterpiece shall we create",
    "Let's paint outside the lines",
    "Time to dream big",
    "Ready to inspire",
    "What vision calls to us",
    "Let's craft magic",
  ],
  "Gen Z": [
    "What's popping",
    "Vibe check successful",
    "Let's slay this",
    "Main character energy",
    "Ready to manifest",
    "Glow up time",
    "Let's get this bread",
  ],
  Conversational: [
    "What's the story",
    "Tell me everything",
    "Ready to connect",
    "What's on your heart",
    "Let's share ideas",
    "What's your perspective",
    "Ready to listen",
  ],
  Analytical: [
    "Data suggests action",
    "Let's examine the evidence",
    "Critical thinking required",
    "Ready to dissect",
    "Pattern recognition activated",
    "Let's analyze the situation",
    "Ready to investigate",
  ],
  Formal: [
    "Greetings and salutations",
    "I await your instruction",
    "Prepared for your direction",
    "At your disposal",
    "Ready to serve",
    "Command accepted",
    "Prepared to proceed",
  ],
  Casual: [
    "Alright, what's next",
    "Ready for whatever",
    "Let's make it happen",
    "What's the vibe",
    "Ready to roll",
    "Let's keep it moving",
    "Whatever you need",
  ],
  Poetic: [
    "What symphony shall we compose",
    "The stars align for us",
    "What tale unfolds today",
    "The universe conspires",
    "What dreams awaken",
    "The canvas awaits",
    "What magic brews",
  ],
  Concise: ["Ready", "Proceed", "Next", "Continue", "Go", "Execute", "Begin"],
  Academic: [
    "Hypothesis ready for testing",
    "Research protocol initiated",
    "Let's explore the literature",
    "Academic inquiry begins",
    "Ready to theorize",
    "Scholarly pursuit awaits",
    "Knowledge quest activated",
  ],
  Organized: [
    "Systematic approach required",
    "Let's establish order",
    "Structure the chaos",
    "Ready to systematize",
    "Organization protocol engaged",
    "Let's categorize everything",
    "Method to the madness",
  ],
  Focused: [
    "Laser focus engaged",
    "Priority identified",
    "Target locked",
    "Concentration maximized",
    "Single-minded pursuit",
    "Mission critical",
    "Zero distractions",
  ],
  Critical: [
    "Critical analysis required",
    "Let's examine the flaws",
    "Quality assurance activated",
    "Standards must be met",
    "Let's scrutinize",
    "Excellence demands perfection",
    "Ready to evaluate",
  ],
};

// Default greetings when no traits are selected
const DEFAULT_GREETINGS = [
  "Ready to explore",
  "What adventure awaits",
  "Let's discover something",
  "Ready to innovate",
  "What mystery unfolds",
  "Ready to create",
  "What journey begins",
  "Ready to transform",
  "What breakthrough calls",
  "Ready to inspire",
];

// Personalized greeting templates that incorporate the user's name
const PERSONALIZED_GREETINGS = [
  "{name}, let's optimize our workflow",
  "Ready to enhance productivity with you, {name}",
  "Time to elevate performance, {name}",
  "{name}, strategic alignment required",
  "Let's maximize efficiency together, {name}",
  "Executive decision time, {name}",
  "Boardroom ready for you, {name}",
  "Prepared to assist you, {name}",
  "{name}, ready to collaborate",
  "Let's achieve excellence, {name}",
  "Ready to deliver results for you, {name}",
  "Time to excel, {name}",
  "Let's innovate together, {name}",
  "Prepared for success with you, {name}",
  "{name}, what's the objective",
  "Target acquired, {name}",
  "Mission parameters set, {name}",
  "Execute the plan, {name}",
  "Priority identified, {name}",
  "Action required, {name}",
  "Let's accomplish this, {name}",
  "Honored to assist you, {name}",
  "{name}, at your service",
  "How may I serve you, {name}",
  "Ready to support your vision, {name}",
  "Your wishes are my command, {name}",
  "Let's honor your vision, {name}",
  "Prepared to help you, {name}",
  "Ready to innovate with you, {name}",
  "{name}, what masterpiece shall we create",
  "Let's paint outside the lines, {name}",
  "Time to dream big with you, {name}",
  "Ready to inspire you, {name}",
  "{name}, what vision calls to us",
  "Let's craft magic, {name}",
  "{name}, what's popping",
  "Vibe check successful for you, {name}",
  "Let's slay this together, {name}",
  "{name}, main character energy",
  "Ready to manifest with you, {name}",
  "Glow up time, {name}",
  "Let's get this bread, {name}",
  "{name}, what's the story",
  "Tell me everything, {name}",
  "Ready to connect with you, {name}",
  "{name}, what's on your heart",
  "Let's share ideas, {name}",
  "{name}, what's your perspective",
  "Ready to listen to you, {name}",
  "Data suggests action, {name}",
  "Let's examine the evidence, {name}",
  "{name}, critical thinking required",
  "Ready to dissect this with you, {name}",
  "Pattern recognition activated, {name}",
  "Let's analyze the situation, {name}",
  "Ready to investigate with you, {name}",
  "{name}, greetings and salutations",
  "I await your instruction, {name}",
  "Prepared for your direction, {name}",
  "{name}, at your disposal",
  "Ready to serve you, {name}",
  "Command accepted, {name}",
  "Prepared to proceed, {name}",
  "{name}, alright, what's next",
  "Ready for whatever you need, {name}",
  "Let's make it happen, {name}",
  "{name}, what's the vibe",
  "Ready to roll with you, {name}",
  "Let's keep it moving, {name}",
  "{name}, whatever you need",
  "{name}, what symphony shall we compose",
  "The stars align for us, {name}",
  "What tale unfolds today, {name}",
  "The universe conspires for you, {name}",
  "{name}, what dreams awaken",
  "The canvas awaits you, {name}",
  "What magic brews, {name}",
  "Ready, {name}",
  "Proceed, {name}",
  "Next, {name}",
  "Continue, {name}",
  "Go, {name}",
  "Execute, {name}",
  "Begin, {name}",
  "{name}, hypothesis ready for testing",
  "Research protocol initiated, {name}",
  "Let's explore the literature, {name}",
  "{name}, academic inquiry begins",
  "Ready to theorize with you, {name}",
  "Scholarly pursuit awaits, {name}",
  "Knowledge quest activated, {name}",
  "{name}, systematic approach required",
  "Let's establish order, {name}",
  "Structure the chaos, {name}",
  "Ready to systematize with you, {name}",
  "{name}, organization protocol engaged",
  "Let's categorize everything, {name}",
  "{name}, method to the madness",
  "{name}, laser focus engaged",
  "Priority identified, {name}",
  "Target locked, {name}",
  "{name}, concentration maximized",
  "Single-minded pursuit, {name}",
  "Mission critical, {name}",
  "{name}, zero distractions",
  "{name}, critical analysis required",
  "Let's examine the flaws, {name}",
  "Quality assurance activated, {name}",
  "{name}, standards must be met",
  "Let's scrutinize this, {name}",
  "{name}, excellence demands perfection",
  "Ready to evaluate with you, {name}",
];

export function ChatGreeting({ onProfileClick }) {
  const profile = useAuthStore((state) => state.profile);

  // Get display name from profile.settings.profile.display_name
  const displayName = profile?.settings?.profile?.display_name || "";

  // Get personality traits from profile.settings.personalization.traits
  const traits = profile?.settings?.personalization?.traits || [];

  // Select greeting based on personality traits (memoized per component instance)
  const { greeting: selectedGreeting, usesNameInGreeting } = useMemo(() => {
    // If no traits are selected, use default greetings
    if (traits.length === 0) {
      const greeting =
        DEFAULT_GREETINGS[Math.floor(Math.random() * DEFAULT_GREETINGS.length)];
      return { greeting, usesNameInGreeting: false };
    }

    // Collect all greetings from selected traits
    const availableGreetings = [];
    traits.forEach((trait) => {
      if (TRAIT_GREETINGS[trait]) {
        availableGreetings.push(...TRAIT_GREETINGS[trait]);
      }
    });

    // If we have greetings from traits, randomly select one
    if (availableGreetings.length > 0) {
      // 30% chance to use a personalized greeting that includes the name
      const usePersonalized = displayName && Math.random() < 0.3;

      if (usePersonalized) {
        // Filter personalized greetings that match the selected traits
        const matchingPersonalized = PERSONALIZED_GREETINGS.filter(
          (personalized) => {
            return traits.some((trait) => {
              const traitGreetings = TRAIT_GREETINGS[trait] || [];
              // Check if any trait greeting is conceptually similar to the personalized one
              return traitGreetings.some(
                (greeting) =>
                  personalized
                    .toLowerCase()
                    .includes(greeting.toLowerCase().split(" ")[0]) ||
                  greeting
                    .toLowerCase()
                    .includes(personalized.toLowerCase().split(" ")[1] || "")
              );
            });
          }
        );

        if (matchingPersonalized.length > 0) {
          const personalizedGreeting =
            matchingPersonalized[
              Math.floor(Math.random() * matchingPersonalized.length)
            ];
          const greetingWithName = personalizedGreeting.replace(
            "{name}",
            displayName
          );
          return { greeting: greetingWithName, usesNameInGreeting: true };
        }
      }

      // Use regular greeting
      const greeting =
        availableGreetings[
          Math.floor(Math.random() * availableGreetings.length)
        ];
      return { greeting, usesNameInGreeting: false };
    }

    // Fallback to default
    const greeting =
      DEFAULT_GREETINGS[Math.floor(Math.random() * DEFAULT_GREETINGS.length)];
    return { greeting, usesNameInGreeting: false };
  }, []); // Memoize greeting selection per component instance

  // Determine appropriate punctuation for all greetings
  const getPunctuation = (greeting) => {
    // Question marks for interrogative phrases
    if (
      greeting.includes("What's") ||
      greeting.includes("How may") ||
      greeting.includes("What shall") ||
      greeting.includes("What masterpiece") ||
      greeting.includes("What vision") ||
      greeting.includes("What story") ||
      greeting.includes("What perspective") ||
      greeting.includes("What's the") ||
      greeting.includes("What symphony") ||
      greeting.includes("What tale") ||
      greeting.includes("What dreams") ||
      greeting.includes("What magic") ||
      greeting.includes("What journey") ||
      greeting.includes("What mystery") ||
      greeting.includes("What adventure") ||
      greeting.includes("What breakthrough") ||
      greeting.includes("What needs") ||
      greeting.includes("How can") ||
      greeting.includes("What do") ||
      greeting.includes("What requires") ||
      greeting.includes("Tell me")
    ) {
      return "?";
    }

    // Exclamation marks for enthusiastic or commanding phrases
    if (
      greeting.includes("Let's") ||
      greeting.includes("Ready") ||
      greeting.includes("Time to") ||
      greeting.includes("Main character") ||
      greeting.includes("Glow up") ||
      greeting.includes("Laser focus") ||
      greeting.includes("Target") ||
      greeting.includes("Execute") ||
      greeting.includes("Mission") ||
      greeting.includes("Priority") ||
      greeting.includes("Action") ||
      greeting.includes("Go") ||
      greeting.includes("Begin") ||
      greeting.includes("Proceed") ||
      greeting.includes("Continue") ||
      greeting.includes("Next") ||
      greeting.includes("Vibe check") ||
      greeting.includes("Slay") ||
      greeting.includes("Manifest") ||
      greeting.includes("Get this bread")
    ) {
      return "!";
    }

    // Period for everything else
    return ".";
  };

  const punctuation = getPunctuation(selectedGreeting);

  return (
    <TooltipProvider>
      <div className="text-center mb-6 text-muted-foreground new-chat-greeting">
        <div className="font-medium mb-1 text-lg sm:text-2xl text-black dark:text-white inline-flex items-center justify-center flex-wrap gap-1">
          <div className="inline-flex">
            {selectedGreeting}
            {!usesNameInGreeting && displayName && (
              <>
                <span className="whitespace-pre">, </span>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <span
                      className="cursor-pointer hover:text-primary transition-colors inline"
                      onClick={onProfileClick}
                    >
                      {displayName}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="rounded-xl">
                    <p>Edit nickname</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
            {!usesNameInGreeting && punctuation}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
